import {
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  Textarea,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import {
  FeedbackType,
  IngestionFolderValidationFragment,
} from '@client/graphql/__generated__/types';
import { ThumbsDown, ThumbsUp } from 'lucide-react';
import { FC, useEffect, useState } from 'react';
import { useIngestionFolderFeedback } from './hooks/useIngestionFolderFeedback';

interface BaseFolderValidationFeedbackProps {
  folderValidation: IngestionFolderValidationFragment;
}

interface ExternalFolderValidationFeedbackProps
  extends BaseFolderValidationFeedbackProps {
  variant: 'external';
}

interface InternalFolderValidationFeedbackProps
  extends BaseFolderValidationFeedbackProps {
  variant: 'internal';
}

export type FolderValidationFeedbackProps =
  | ExternalFolderValidationFeedbackProps
  | InternalFolderValidationFeedbackProps;

export const FolderValidationFeedback: FC<FolderValidationFeedbackProps> = ({
  folderValidation,
  variant,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [comment, setComment] = useState('');
  const [selectedFeedbackType, setSelectedFeedbackType] =
    useState<FeedbackType | null>(null);

  const {
    existingFeedback,
    createOrUpdateFeedback,
    createOrUpdateFeedbackLoading,
  } = useIngestionFolderFeedback({
    ingestionFolderValidationId: folderValidation.id,
    variant,
  });

  const handleSubmitFeedback = () => {
    if (!selectedFeedbackType) return;

    createOrUpdateFeedback(
      {
        ingestionFolderValidationId: folderValidation.id,
        feedbackType: selectedFeedbackType,
        comment: comment.trim() || undefined,
      },
      onClose
    );
  };

  const handleFeedbackTypeSelect = (feedbackType: FeedbackType) => {
    setSelectedFeedbackType(feedbackType);
    // Always open popover for comment
    onOpen();
  };

  const handleClose = () => {
    setComment(existingFeedback?.comment || '');
    setSelectedFeedbackType(existingFeedback?.feedbackType || null);
    onClose();
  };

  useEffect(() => {
    if (existingFeedback?.feedbackType) {
      setSelectedFeedbackType(existingFeedback.feedbackType);
    }
    if (existingFeedback?.comment) {
      setComment(existingFeedback.comment);
    }
  }, [existingFeedback]);

  return (
    <Box>
      <Card size="sm" variant="outline">
        <CardBody px={3} py={2}>
          <Flex align="center" gap={2} justify="space-between">
            <Text color="compliance.text.secondary" fontSize="sm">
              How'd we do?
            </Text>
            <Flex>
              <IconButton
                aria-label="Thumbs up"
                color={
                  selectedFeedbackType === FeedbackType.THUMBS_UP
                    ? 'compliance.button.secondary.text'
                    : 'compliance.text.muted'
                }
                icon={<ThumbsUp size={16} />}
                size="xs"
                variant="link"
                onClick={() => handleFeedbackTypeSelect(FeedbackType.THUMBS_UP)}
              />
              <IconButton
                aria-label="Thumbs down"
                color={
                  selectedFeedbackType === FeedbackType.THUMBS_DOWN
                    ? 'compliance.button.secondary.text'
                    : 'compliance.text.muted'
                }
                icon={<ThumbsDown size={16} />}
                size="xs"
                variant="link"
                onClick={() =>
                  handleFeedbackTypeSelect(FeedbackType.THUMBS_DOWN)
                }
              />
            </Flex>
          </Flex>
        </CardBody>
      </Card>

      <Popover isOpen={isOpen} placement="bottom-end" onClose={handleClose}>
        <PopoverTrigger>
          <Box />
        </PopoverTrigger>
        <PopoverContent>
          <PopoverArrow />
          <PopoverBody>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel color="compliance.text.secondary" size="sm">
                  {selectedFeedbackType === FeedbackType.THUMBS_UP
                    ? 'Tell us what worked well (optional)'
                    : 'Tell us what could be improved (optional)'}
                </FormLabel>
                <Textarea
                  placeholder={
                    selectedFeedbackType === FeedbackType.THUMBS_UP
                      ? 'What did you like about this validation?'
                      : 'What could be improved?'
                  }
                  rows={3}
                  size="sm"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                />
              </FormControl>
              <HStack spacing={2} w="full">
                <Button
                  flex={1}
                  size="sm"
                  variant="outline"
                  onClick={handleClose}
                >
                  Cancel
                </Button>
                <Button
                  colorScheme="blue"
                  flex={1}
                  isLoading={createOrUpdateFeedbackLoading}
                  size="sm"
                  onClick={() => void handleSubmitFeedback()}
                >
                  Submit
                </Button>
              </HStack>
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </Box>
  );
};
